import React from "react";
import classNames from "classnames";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import FreshbooksLogo from "@static/images/logos/freshbooks-logo-full.png";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { CheckIcon } from "@icons";
import { BetaTag } from "@design-system/BetaTag/BetaTag";
import { HStack } from "@design-system/HStack/HStack";
import { FlatRatePlan } from "@common/schemas/billing.schema";
import { PlansRow } from "./PlansRow";
import grid from "./Plans.module.scss";
import styles from "./PlanHeader.module.scss";
import { HighlightFeatures, isBetaPlanFeature } from "./plansData";
import { KeyFeatureAppendixIcons } from "./KeyFeatureAppendixIcons";

interface PlanHeaderProps {
    plan: FlatRatePlan;
    action: React.ReactNode;
    highlights: HighlightFeatures[];
    price: React.ReactNode;
    titleAddon?: React.ReactNode;
}

const KeyFeatureHeader = ({ plan }: { plan: FLAT_RATE_PLANS }) => {
    const { billingCatalogV3 } = useTypedFlags();

    switch (plan) {
        case FLAT_RATE_PLANS.FREE:
            return (
                <>
                    {billingCatalogV3
                        ? undefined
                        : "Up to $25k annual business expenses"}
                </>
            );
        case FLAT_RATE_PLANS.BASIC:
            return (
                <>
                    Everything in <span className={styles.semiBold}>Free</span>,
                    and
                </>
            );
        case FLAT_RATE_PLANS.PLUS:
            return (
                <>
                    Everything in <span className={styles.semiBold}>Basic</span>
                    , and
                </>
            );
        case FLAT_RATE_PLANS.ADVANCED:
            return (
                <>
                    Everything in <span className={styles.semiBold}>Plus</span>,
                    and
                </>
            );
        case FLAT_RATE_PLANS.ENTERPRISE:
        case FLAT_RATE_PLANS.FRESHBOOKS:
            return (
                <>
                    Everything in{" "}
                    <span className={styles.semiBold}>Advanced</span>, and
                </>
            );
    }
};

const TargetAudianceHeader = ({ plan }: { plan: FLAT_RATE_PLANS }) => {
    switch (plan) {
        case FLAT_RATE_PLANS.FREE:
            return "New business and sole props";
        case FLAT_RATE_PLANS.BASIC:
            return "Full-time businesses needing automation";
        case FLAT_RATE_PLANS.PLUS:
            return "Growing businesses with accrual needs";
        case FLAT_RATE_PLANS.ADVANCED:
            return "Businesses with complex multi-entity or accrual needs";
        case FLAT_RATE_PLANS.ENTERPRISE:
            return "Complex businesses needing tailored support";
        case FLAT_RATE_PLANS.FRESHBOOKS:
            return (
                <span className={styles.freshbooksSubHeader}>
                    2025 books included for free
                </span>
            );
    }
};

const EntityCountHeader = ({ plan }: { plan: FLAT_RATE_PLANS }) => {
    const { billingCatalogV3 } = useTypedFlags();

    switch (plan) {
        case FLAT_RATE_PLANS.FREE:
            return "1 entity";
        case FLAT_RATE_PLANS.BASIC:
            return "1 entity";
        case FLAT_RATE_PLANS.PLUS:
            if (billingCatalogV3) {
                return (
                    <>
                        <span>Unlimited</span>
                        <span className="text-gray-700 mt-2">
                            New entities start free for your first 250
                            transactions per year, then $50/m
                        </span>
                    </>
                );
            }
            return "Up to 5 entities";
        case FLAT_RATE_PLANS.ADVANCED:
            return (
                <>
                    <span>Unlimited</span>
                    <span className="text-gray-700 mt-2">
                        New entities start free for your first 250 transactions
                        per year, then $50/m
                    </span>
                </>
            );
        case FLAT_RATE_PLANS.ENTERPRISE:
            return "Unlimited";
        case FLAT_RATE_PLANS.FRESHBOOKS:
            return "Unlimited";
    }
};

export const PlanHeader: React.FC<PlanHeaderProps> = ({
    plan,
    highlights,
    action,
    price,
    titleAddon,
}) => {
    const { billingCatalogV3 } = useTypedFlags();

    const freeTierLimit = billingCatalogV3 ? "250" : "$25k";

    return (
        <article className={classNames(grid.wrapper, styles.planHeader)}>
            <header className={grid.wrapper}>
                <PlansRow sticky first>
                    <HStack gap={12} align="center" wrap justify="between">
                        <h3 className={styles.name}>
                            {plan.plan === FLAT_RATE_PLANS.FRESHBOOKS ? (
                                <img
                                    src={FreshbooksLogo}
                                    width={115}
                                    alt="Freshbooks logo"
                                />
                            ) : (
                                flatRatePlanNames[plan.plan]
                            )}
                        </h3>
                        {titleAddon}
                    </HStack>
                </PlansRow>
                <PlansRow sticky>{price}</PlansRow>

                <PlansRow className="pr-3" sticky>
                    <TargetAudianceHeader plan={plan.plan} />
                </PlansRow>
                {action && (
                    <PlansRow className={styles.action} sticky>
                        {action}
                    </PlansRow>
                )}
            </header>

            <PlansRow className={styles.limit}>
                {plan.plan === FLAT_RATE_PLANS.FREE ? (
                    <>{freeTierLimit}</>
                ) : (
                    "Unlimited"
                )}
            </PlansRow>

            <PlansRow>
                <KeyFeatureHeader plan={plan.plan} />
            </PlansRow>

            <PlansRow className="align-content-start">
                <ul className={styles.highlights}>
                    {highlights.map((highlight) => (
                        <li key={highlight?.toString()}>
                            <span className={styles.feature}>
                                <CheckIcon className="icon-color-blue-600" />
                                <span>
                                    {highlight}
                                    {isBetaPlanFeature(highlight) ? (
                                        <BetaTag />
                                    ) : null}
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            </PlansRow>
            <PlansRow>
                <KeyFeatureAppendixIcons plan={plan.plan} />
            </PlansRow>
            <PlansRow className={styles.limit} last>
                <EntityCountHeader plan={plan.plan} />
            </PlansRow>
        </article>
    );
};
